import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { api_routes, db_tables } from '@app/consts';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ReviewPlansService } from '../../services/review-plans.service';
import { defer, finalize, forkJoin } from 'rxjs';
import { ReviewPlanGoalTypeWeight, ReviewPlanGoalTypeWeightSubmit } from '../../models/review-plan.model';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { ReviewPlanGoalTypeDialogComponent } from '../review-plan-goal-type-dialog/review-plan-goal-type-dialog.component';

@Component({
  selector: 'app-review-plan-goal-type-weights',
  templateUrl: './review-plan-goal-type-weights.component.html',
  styleUrls: ['./review-plan-goal-type-weights.component.scss']
})
export class ReviewPlanGoalTypeWeightsComponent implements OnInit {

  @Input() reviewPlanId: string;

  public columns: any[] = [
    { field: "goalType", subField: "name", title: this.translate.instant("GoalType")},
    { field: "weight", title: this.translate.instant("Weight"), type: "percentage", percentageMaxValue: 1, tableId: "tbl_RpGtWeight", },
    { field: "hasRating", title: "Has Rating", type: "booleanToggle", tableId: "tbl_RpGtHasRating" },
    { field: "hasComments", title: "Has Comments", type: "booleanToggle", tableId: "tbl_RpGtHasComments" },
    { field: "childGoalHasComments", title: "Child Goal Has Comments", type: "booleanToggle", tableId: "tbl_RpGtChildGoalHasComments" },
    { field: "childGoalHasRating", title: "Child Goal Has Rating", type: "booleanToggle", tableId: "tbl_RpGtChildGoalHasRating" },
  ];

  filterCategories: any[] = [
    { field: "goalType", subField: "name", title: this.translate.instant("GoalType"), dataType: "SpecialLookup", lookupCode: 'GOAL_PLAN'},
    { field: "weight", title: this.translate.instant("Weight"), dataType: "Percentage"},
    { field: "hasRating", title: "Has Rating", tableId: "tbl_RpGtHasRating", dataType: "ProvidedList", listOptions: {
      list: [{id: true, name: 'True'}, {id: false, name: 'False'},],
      id: 'id',
      name: 'name'
    }},
    { field: "hasComments", title: "Has Comments", tableId: "tbl_RpGtHasComments", dataType: "ProvidedList", listOptions: {
      list: [{id: true, name: 'True'}, {id: false, name: 'False'},],
      id: 'id',
      name: 'name'
    }},
    { field: "childGoalHasComments", title: "Child Goal Has Comments", tableId: "tbl_RpGtChildGoalHasComments", dataType: "ProvidedList", listOptions: {
      list: [{id: true, name: 'True'}, {id: false, name: 'False'},],
      id: 'id',
      name: 'name'
    }},
    { field: "childGoalHasRating", title: "Child Goal Has Rating", tableId: "tbl_RpGtChildGoalHasRating", dataType: "ProvidedList", listOptions: {
      list: [{id: true, name: 'True'}, {id: false, name: 'False'},],
      id: 'id',
      name: 'name'
    }},
  ];

  sortableColumns: any[] = [
    { field: "goalType", sortValue: "goalTypeName"},
    { field: "weight", sortValue: "weight"},
    { field: "hasRating", sortValue: "hasRating"},
    { field: "hasComments", sortValue: "hasComments"},
    { field: "childGoalHasComments", sortValue: "childGoalHasComments"},
    { field: "childGoalHasRating", sortValue: "childGoalHasRating"},
  ];

  public bindingType: String = "array";
  public view: any;
  public gridData: any;
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 5;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: any;
  formValid: any;
  sortString: string;
  searchValue: string;
  searchFilterString: string;
  filterString: any;
  getGoalsRequest: any;

  constructor(
    private dialog: MatDialog,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private overlayService: OverlayService,
    private reviewPlansService: ReviewPlansService
  ) { }

  ngOnInit(): void {
    this.view = this.getReviewPlanGoalTypeWeights();
  }

  getReviewPlanGoalTypeWeights() {
    this.isLoading = true;

    let filter;
    if(this.searchFilterString) {
      filter = this.searchFilterString;
    }
    if(this.filterString) {
      if(filter){
        filter = `${filter} AND ${this.filterString}`;
      }
      else {
        filter = `${this.filterString}`;
      }
    }

    this.getGoalsRequest = this.reviewPlansService.getReviewPlanGoalTypeWeights(this.reviewPlanId, this.pageSize, this.skip, this.sortString, filter)
      .pipe(
        finalize( () => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
          this.gridData = res.data;
        }
      );
  }

  getReviewPlanGoalTypeWeightVerbose(reviewPlanGoalTypeWeight?: ReviewPlanGoalTypeWeight) {
    if(reviewPlanGoalTypeWeight === undefined){
      this.openReviewPlanGoalTypeWeightDialog();
    }
    else {
      this.overlayService.show('Loading');

      this.reviewPlansService.getReviewPlanGoalTypeWeight(this.reviewPlanId, reviewPlanGoalTypeWeight.id)
      .pipe(
        finalize( () => {
          this.overlayService.hide();
        })
      )
      .subscribe(
        (res) => {
          this.openReviewPlanGoalTypeWeightDialog(res);
        }
      );

    }
  }

  openReviewPlanGoalTypeWeightDialog(reviewPlanGoalTypeWeight?: ReviewPlanGoalTypeWeight) {
    let formData = reviewPlanGoalTypeWeight ? JSON.parse(JSON.stringify(reviewPlanGoalTypeWeight)) : null;

    if(formData !== null){
      formData.goalTypeId = formData.goalType.id;
    } 

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formId: 'frm_6pboXzQqCocJNw',
      formData: formData
    };

    this.dialogRef = this.dialog.open(ReviewPlanGoalTypeDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      reviewPlanGoalTypeWeight ? this.updateReviewPlanGoalTypeWeight(event) : this.createNewReviewPlanGoalTypeWeight(event)
    });
  }

  createNewReviewPlanGoalTypeWeight(formData: ReviewPlanGoalTypeWeightSubmit){
    this.overlayService.show();

    this.reviewPlansService.addReviewPlanGoalTypeWeight(this.reviewPlanId, formData)
    .pipe(
        finalize(() => this.overlayService.hide())
    )
    .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(res);
          this.getReviewPlanGoalTypeWeights();
        }
    );
  }

  updateReviewPlanGoalTypeWeight(formData: ReviewPlanGoalTypeWeightSubmit){
    this.overlayService.show();

    this.reviewPlansService.updateReviewPlanGoalTypeWeight(this.reviewPlanId, formData.id, formData)
    .pipe(
        finalize(() => this.overlayService.hide())
    )
    .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(res);
          this.getReviewPlanGoalTypeWeights();
        }
    );
  }

  deleteReviewPlanGoalTypeWeight(goalTypeWeights: string[]) {
    const observables = goalTypeWeights.map(selectedItem => defer(() => this.reviewPlansService.deleteReviewPlanGoalTypeWeight(this.reviewPlanId, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.view = this.getReviewPlanGoalTypeWeights();
        this.clearSelectedItems = !this.clearSelectedItems;
        this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.view = this.getReviewPlanGoalTypeWeights();
  }

  search(newSearchString: string){
    this.searchValue = newSearchString;

    let variants = this.searchValue.split(' ').filter(value => value);
    this.searchFilterString = '';
    variants.map(variant => {
        if (variant) {
            if (this.searchFilterString.length > 2) {
                this.searchFilterString += ' AND ';
            }
            this.searchFilterString += `(GoalType.Name like "${variant}")`;
        }
    });

    this.getGoalsRequest.unsubscribe();
    this.getReviewPlanGoalTypeWeights();
  }

  sortChange(sortString: string){
    this.sortString = sortString;
    this.getReviewPlanGoalTypeWeights();
  }

  filterCallback(filterString: string) {
    this.filterString = filterString;
    this.skip = 0;;
    this.getReviewPlanGoalTypeWeights();
  }

}
